import { FaaReportOptions } from '../data-models/faa-report-options.model';
import { CreateFaaReportRequest } from '../request-models/create-faa-report-request.model';
import { UpdateFaaReportRequest } from '../request-models/update-faa-report-request.model';
import { isRecord } from './is-record';
import { nullableBoolean, omitUndefined } from './wire-value-utils';

/**
 * Available FAA report types for backend API
 * Note: 'complete_faa' is the default backend value
 */
export const FAA_REPORT_TYPE_VALUES = [
  { value: 'complete_faa', label: 'Complete FAA Report' },
  { value: 'part137', label: 'Part 137 Operations' },
  { value: 'part107', label: 'Part 107 Compliance' },
  { value: 'waiver', label: 'Waiver Operations' },
  { value: 'incident', label: 'Incident Reports' }
] as const;

/**
 * Reads a report's `options` block: `undefined` when the hub didn't send it, `null` when it sent `null`.
 * A flag the hub left out reads as `false` (the block is always sent whole).
 */
export function toFaaReportOptions(value: unknown): FaaReportOptions | null | undefined {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    return undefined;
  }
  return {
    includeFlightLogs: nullableBoolean(value['includeFlightLogs']) ?? false,
    includeSprayRecords: nullableBoolean(value['includeSprayRecords']) ?? false,
    includePilotInfo: nullableBoolean(value['includePilotInfo']) ?? false,
    includeAircraftInfo: nullableBoolean(value['includeAircraftInfo']) ?? false,
  };
}

/** The create body: only the allowlisted keys that are set. Never send a `FaaReport`. */
export function toCreateFaaReportRequest(request: CreateFaaReportRequest): CreateFaaReportRequest {
  return omitUndefined({
    name: request.name,
    startDate: request.startDate,
    endDate: request.endDate,
    options: copyOptions(request.options),
  });
}

/** The update body (hub FAA_REPORT_WRITABLE: `name`, `options`), with only the keys that are set. */
export function toUpdateFaaReportRequest(request: UpdateFaaReportRequest): UpdateFaaReportRequest {
  return omitUndefined({
    name: request.name,
    options: request.options === undefined ? undefined : copyOptions(request.options),
  });
}

function copyOptions(options: FaaReportOptions): FaaReportOptions {
  return {
    includeFlightLogs: options.includeFlightLogs,
    includeSprayRecords: options.includeSprayRecords,
    includePilotInfo: options.includePilotInfo,
    includeAircraftInfo: options.includeAircraftInfo,
  };
}
